import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { renderHighlightedPlainText } from './highlight';

function parseEml(raw: string) {
  // Very lightweight parser: headers until blank line, then raw body.
  // This won't decode multipart/attachments; it's intended as a quick preview.
  const lines = (raw || '').split(/\r?\n/);
  const headers: Record<string, string> = {};

  let i = 0;
  let currentKey: string | null = null;
  for (; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') { i++; break; }

    // header folding
    if ((line.startsWith(' ') || line.startsWith('\t')) && currentKey) {
      headers[currentKey] = (headers[currentKey] + ' ' + line.trim()).trim();
      continue;
    }

    const idx = line.indexOf(':');
    if (idx <= 0) continue;
    const key = line.slice(0, idx).trim();
    const val = line.slice(idx + 1).trim();
    currentKey = key;
    // Preserve repeated headers (e.g. multiple Received lines)
    headers[key] = headers[key] ? `${headers[key]}\n${val}` : val;
  }

  const body = lines.slice(i).join('\n');
  return { headers, body };
}

export function EmlViewer({ url, height = 'calc(85vh - 44px)', highlightText }: { url: string; height?: string | number; highlightText?: string | null }) {
  const [raw, setRaw] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setRaw('');

    (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error('Failed to fetch EML');
        const txt = await resp.text();
        if (cancelled) return;
        setRaw(txt);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load EML');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  const parsed = useMemo(() => parseEml(raw), [raw]);

  function getHeader(name: string) {
    const target = (name || '').toLowerCase();
    if (!target) return '';
    const direct = parsed.headers[name];
    if (direct != null) return direct;
    const key = Object.keys(parsed.headers).find(k => k.toLowerCase() === target);
    return key ? (parsed.headers[key] || '') : '';
  }

  function formatMaybeEmailDate(value: string) {
    const v = (value || '').trim();
    if (!v) return '';
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleString();
    return v;
  }

  function getBestDate() {
    const candidates = [
      getHeader('Date'),
      getHeader('Sent'),
      getHeader('Delivery-date'),
      getHeader('X-Original-Date'),
      getHeader('X-Sent'),
      getHeader('X-Date')
    ].map(s => (s || '').trim()).filter(Boolean);

    for (const c of candidates) {
      const formatted = formatMaybeEmailDate(c);
      if (formatted) return formatted;
    }

    // Fallback: try to parse date from Received headers (text after last ';')
    const received = (getHeader('Received') || '').trim();
    if (received) {
      const lines = received.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i];
        const part = line.split(';').pop()?.trim() || '';
        const formatted = formatMaybeEmailDate(part);
        if (formatted) return formatted;
      }
    }

    return '';
  }

  if (error) return <div style={{ padding: 12, color: 'red' }}>{error}</div>;
  if (!raw) return <div style={{ padding: 12 }}>Loading email…</div>;

  const headerKeys = ['From', 'To', 'Cc', 'Bcc', 'Date', 'Subject'];
  const bestDate = getBestDate();

  return (
    <div style={{ height, overflow: 'auto', padding: 12, fontSize: 13, flex: 1, minHeight: 0 }}>
      <div style={{ border: '1px solid #e0e6ed', borderRadius: 6, padding: 10, background: '#f7fafd', marginBottom: 10 }}>
        {headerKeys.map(k => (
          <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            <div style={{ width: 70, fontWeight: 600 }}>{k}:</div>
            <div style={{ flex: 1, wordBreak: 'break-word' }}>
              {renderHighlightedPlainText(k === 'Date' ? bestDate : getHeader(k), highlightText)}
            </div>
          </div>
        ))}
      </div>
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'system-ui, Arial, sans-serif' }}>{renderHighlightedPlainText(parsed.body, highlightText)}</pre>
    </div>
  );
}
